/**
 * Pickup checklist on the booking page of an OPEN booking (scanning merchants).
 *
 * Every physical line gets a tick once its item is scanned:
 *   - the allocated unit                       -> tick
 *   - an identical unit (same type + size)     -> offer to swap it in, then tick
 *   - a unit for an unallocated line           -> allocate it, then tick
 *   - a pool / unlimited product barcode       -> tick one unit of that line
 * Swaps go through the existing change_booking_allocation RPC, so conflicts,
 * blocked and inactive items are refused with the RPC's reason.
 *
 * Start stays the page's own button; the page asks the checklist which lines
 * are still unticked (onProgress) to warn before starting.
 * See docs/BARCODE_SCANNING_SPEC.md, "Flow B" and "Flow C".
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Circle, Loader2, ScanLine, ArrowRightLeft } from 'lucide-react';
import ScanInput from './ScanInput';
import { physicalLines, lineLabel, untickedLines, isUnallocatedUnitLine, type PhysicalLine } from './scanLines';
import { useAuth } from '../../contexts/AuthContext';
import { useBarcodeScanning, useScanHandler } from '../../contexts/ScanContext';
import { resolveBarcode, unitLabel, type ScannedUnit } from '../../services/barcode.service';
import { callChangeAllocation, formatAllocationFailure } from '../../lib/allocationRpc';
import { insertAuditLog } from '../../services/bookingAuditLog.service';

export interface PickupScan {
  unit?: ScannedUnit;
  productTypeId?: string;
  at: number;
}

interface PickupChecklistProps {
  booking: any;
  /** Reload the booking after an allocation change. */
  onBookingChanged: () => Promise<void> | void;
  /** A scan handed over from the global handler (navigation state). */
  initialScan?: PickupScan | null;
  /** Called once the handed-over scan was applied, so the page can drop it. */
  onInitialScanConsumed?: () => void;
  onProgress: (unticked: PhysicalLine[]) => void;
}

interface PendingSwap {
  line: PhysicalLine;
  unit: ScannedUnit;
}

export default function PickupChecklist({ booking, onBookingChanged, initialScan, onInitialScanConsumed, onProgress }: PickupChecklistProps) {
  const { t } = useTranslation();
  const { user, profile, merchantId } = useAuth();
  const { feedback } = useBarcodeScanning();

  const [ticks, setTicks] = useState<Record<string, number>>({});
  const [notice, setNotice] = useState<{ text: string; tone: 'error' | 'ok' } | null>(null);
  const [pendingSwap, setPendingSwap] = useState<PendingSwap | null>(null);
  const [working, setWorking] = useState(false);

  const lines = useMemo(() => physicalLines(booking?.booking_items), [booking]);

  // New booking -> fresh checklist. Reloads of the same booking keep the ticks.
  useEffect(() => {
    setTicks({});
    setNotice(null);
    setPendingSwap(null);
  }, [booking?.id]);

  useEffect(() => {
    onProgress(untickedLines(lines, ticks));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, ticks]);

  const tickLine = (lineId: string, quantity: number) =>
    setTicks((prev) => ({ ...prev, [lineId]: Math.min(quantity, (prev[lineId] || 0) + 1) }));

  const handleUnit = (unit: ScannedUnit) => {
    const own = lines.find((l) => l.inventory_item_id === unit.id);
    if (own) {
      tickLine(own.id, own.quantity);
      feedback('ok');
      setNotice({ text: t('scan.ticked', { item: lineLabel(own) }), tone: 'ok' });
      return;
    }

    const sameKind = (l: PhysicalLine) =>
      unit.product_type_ids.includes(l.product_type_id) && (l.size ?? null) === (unit.size ?? null);
    const open = lines.filter((l) => (ticks[l.id] || 0) < l.quantity && sameKind(l));
    // Prefer a line with nothing allocated yet, then the first unticked one.
    const target = open.find(isUnallocatedUnitLine) ?? open.find((l) => !!l.inventory_item_id);

    if (!target) {
      feedback('error');
      setNotice({
        text: t('scan.notInBooking', { item: `${unit.product_name}${unit.size ? ` (${unit.size})` : ''} ${unitLabel(unit)}` }),
        tone: 'error',
      });
      return;
    }

    feedback('ok');
    setNotice(null);
    setPendingSwap({ line: target, unit });
  };

  const confirmSwap = async () => {
    if (!pendingSwap) return;
    const { line, unit } = pendingSwap;
    setWorking(true);
    try {
      const result = await callChangeAllocation({
        p_booking_item_id: line.id,
        p_new_inventory_item_id: unit.id,
        p_swap_with_booking_item_id: null,
        p_current_inventory_item_id: null,
        p_skip_buffer_check: false,
      });
      if (!result.ok) {
        feedback('error');
        setNotice({ text: formatAllocationFailure(result, t), tone: 'error' });
        setPendingSwap(null);
        return;
      }

      insertAuditLog({
        bookingId: booking.id,
        merchantId: booking.merchant_id || merchantId,
        locationId: booking.location_id,
        changeType: 'inventory_reallocated',
        changeDetails: {
          product_name: line.item_name,
          from_item: line.unit_label,
          to_item: unitLabel(unit),
          swap: false,
          source: 'barcode_scan',
        },
        userId: user?.id,
        userEmail: profile?.full_name || user?.email || null,
        triggeredBy: 'admin_panel',
      });

      setPendingSwap(null);
      tickLine(line.id, line.quantity);
      setNotice({ text: t('scan.swapped', { item: unitLabel(unit) }), tone: 'ok' });
      await onBookingChanged();
    } catch (err) {
      console.error('Scan swap failed:', err);
      feedback('error');
      setNotice({ text: t('changeAllocation.failedToChange'), tone: 'error' });
      setPendingSwap(null);
    } finally {
      setWorking(false);
    }
  };

  const handleProduct = (productTypeId: string, productName: string) => {
    const line = lines.find(
      (l) => !l.inventory_item_id && !isUnallocatedUnitLine(l) && l.product_type_id === productTypeId && (ticks[l.id] || 0) < l.quantity
    );
    if (!line) {
      feedback('error');
      setNotice({ text: t('scan.notInBooking', { item: productName }), tone: 'error' });
      return;
    }
    tickLine(line.id, line.quantity);
    feedback('ok');
    setNotice({ text: t('scan.ticked', { item: lineLabel(line) }), tone: 'ok' });
  };

  // One scan at a time: a second quick scan would work from the same stale lines.
  const scanning = useRef(false);
  const runScan = async (code: string) => {
    if (working || scanning.current) return;
    scanning.current = true;
    try {
      await handleScanCode(code);
    } finally {
      scanning.current = false;
    }
  };
  useScanHandler(runScan);

  const handleScanCode = async (code: string) => {
    const { data: res, error } = await resolveBarcode(code, merchantId);
    if (error || !res) {
      feedback('error');
      setNotice({ text: t('scan.lookupFailed', { error: error || '' }), tone: 'error' });
      return;
    }
    if (res.kind === 'unknown') {
      feedback('error');
      setNotice({ text: t('scan.unknownBarcode', { code }), tone: 'error' });
      return;
    }
    if (res.kind === 'unit') handleUnit(res.unit);
    else handleProduct(res.product.id, res.product.product_name);
  };

  // A scan handed over from the global handler: apply it once the lines are there.
  const appliedScan = useRef<number | null>(null);
  useEffect(() => {
    if (!initialScan || appliedScan.current === initialScan.at || lines.length === 0) return;
    appliedScan.current = initialScan.at;
    if (initialScan.productTypeId) {
      const line = lines.find((l) => l.product_type_id === initialScan.productTypeId);
      handleProduct(initialScan.productTypeId, line?.item_name || '');
    } else if (initialScan.unit) {
      handleUnit(initialScan.unit);
    }
    onInitialScanConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScan?.at, lines.length]);

  const done = lines.filter((l) => (ticks[l.id] || 0) >= l.quantity).length;

  return (
    <div className="card p-md sm:p-lg" data-testid="pickup-checklist">
      <div className="flex flex-wrap items-center justify-between gap-sm mb-md">
        <div className="flex items-center gap-sm">
          <ScanLine className="w-5 h-5 text-blue" />
          <h3 className="font-semibold text-primary">{t('scan.pickupTitle')}</h3>
        </div>
        <span className="text-sm text-secondary">{t('scan.ofQuantity', { done, total: lines.length })}</span>
      </div>

      <ul className="space-y-xs">
        {lines.map((line) => {
          const isDone = (ticks[line.id] || 0) >= line.quantity;
          return (
            <li
              key={line.id}
              className={`flex items-center gap-sm p-sm rounded-lg border ${isDone ? 'border-success/40 bg-success/5' : 'border-primary'}`}
            >
              {isDone ? <Check className="w-4 h-4 text-success flex-shrink-0" /> : <Circle className="w-4 h-4 text-secondary flex-shrink-0" />}
              <span className={`text-sm flex-1 ${line.in_bundle ? 'pl-md' : ''}`}>
                {lineLabel(line)}
                {isUnallocatedUnitLine(line) && !isDone && (
                  <span className="text-xs text-warning ml-sm">{t('scan.notAllocated')}</span>
                )}
              </span>
              {line.quantity > 1 && (
                <span className="text-xs text-secondary">
                  {t('scan.ofQuantity', { done: ticks[line.id] || 0, total: line.quantity })}
                </span>
              )}
              {/* Manual tick for a label that won't scan. An unallocated unit line
                  needs a scanned item first. */}
              {!isDone && !isUnallocatedUnitLine(line) && (
                <button type="button" className="text-xs text-blue hover:underline" onClick={() => tickLine(line.id, line.quantity)}>
                  {t('scan.tickManually')}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {pendingSwap && (
        <div className="mt-md p-md bg-blue/5 border border-blue/30 rounded-lg" data-testid="pickup-swap-prompt">
          <div className="flex items-start gap-sm">
            <ArrowRightLeft className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" />
            <p className="text-sm text-primary flex-1">
              {pendingSwap.line.unit_label
                ? t('scan.swapPrompt', { from: pendingSwap.line.unit_label, to: unitLabel(pendingSwap.unit), item: pendingSwap.line.item_name })
                : t('scan.allocatePrompt', { to: unitLabel(pendingSwap.unit), item: pendingSwap.line.item_name })}
            </p>
          </div>
          <div className="flex justify-end gap-sm mt-sm">
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setPendingSwap(null)} disabled={working}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={confirmSwap} disabled={working} data-testid="pickup-swap-confirm">
              {working && <Loader2 className="w-4 h-4 animate-spin" />}
              {pendingSwap.line.unit_label ? t('scan.swapConfirm') : t('scan.allocateConfirm')}
            </button>
          </div>
        </div>
      )}

      {notice && (
        <p className={`text-sm mt-md ${notice.tone === 'error' ? 'text-danger' : 'text-success'}`}>{notice.text}</p>
      )}

      <div className="mt-md">
        <ScanInput onSubmit={runScan} testId="pickup-scan-input" />
      </div>

      <p className="text-xs text-secondary mt-md">{t('scan.pickupHint')}</p>
    </div>
  );
}
